import * as React from 'react'

import createContext from 'modules/core/hooks/createContext'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useModuleAvailabilities } from 'modules/core/hooks/useModuleAvailabilities'

import {
  type GetUpsellingDataReturns,
  useGetUpsellingData,
} from 'modules/upselling_kit/hooks/useGetUpsellingData'
import { UpsellingKitModule } from 'modules/upselling_kit/types'

type UpsellingKitComponent = UpsellingKitModule & {
  title?: string
  description?: string
}

type Context = UpsellingKitModule &
  UpsellingKitComponent & {
    upsellData: Omit<
      GetUpsellingDataReturns,
      'isAllowed' | 'loading' | 'moduleAvailable' | 'featureAvailable'
    >
    isAllowed: boolean
    isAlreadyInstalled: boolean
    isFeatureAlreadyAvailable: boolean
    loading: boolean
    moduleAvailable: boolean
  }

const { context: UpsellContext, hook: useUpsell } = createContext<Context>()

const UpsellProvider: React.FC<React.PropsWithChildren<UpsellingKitComponent>> = ({
  children,
  moduleRequested,
  requestSource,
  featureRequested,
  requestReason,
  title,
  description,
  onClick,
  onClose,
}) => {
  const { loading, moduleAvailable, featureAvailable, ...upsellData } = useGetUpsellingData({
    moduleRequested,
    featureRequested,
    title,
    description,
  })

  const { canRequestUpsell } = useCurrentSession()
  const { moduleAvailabilities } = useModuleAvailabilities()

  const isAlreadyInstalled = moduleAvailabilities.find(
    (module) => module.id === moduleRequested
  )?.available

  const isFeatureAlreadyAvailable = featureAvailable

  return (
    <UpsellContext.Provider
      value={{
        moduleRequested,
        requestSource,
        featureRequested,
        requestReason,
        isAllowed: canRequestUpsell,
        isAlreadyInstalled: isAlreadyInstalled ?? false,
        isFeatureAlreadyAvailable: isFeatureAlreadyAvailable ?? false,
        upsellData,
        moduleAvailable: moduleAvailable,
        onClick,
        onClose,
        loading,
      }}
    >
      {children}
    </UpsellContext.Provider>
  )
}

export { UpsellProvider, useUpsell }
